import { useEffect, useRef, useState, useCallback } from "react";
import { Mic, MicOff, X, Loader2, Check, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * FloatingMicButton — toggle on/off voice capture with:
 *   • Real-time audio level meter (Web Audio API analyser)
 *   • Auto-pause after ~12s of silence (RMS below threshold) — stops & transcribes
 *   • Audible start/stop tones
 *   • Live timer + visible REC indicator
 *
 * Streams the resulting webm/mp4 blob to /api/clinical-capture/transcribe →
 * /clinical-capture/structure → opens the confirm modal where the therapist
 * can edit any of the 11 fields before saving to session.clinical_capture[]
 * (+ optionally client.clinical_findings).
 *
 * Optional `preTaggedMuscle` and `preTaggedSide` props skip the floating button
 * and open the modal directly with those fields pre-filled (used by BodyDiagram).
 */

const SILENCE_RMS_THRESHOLD = 0.012; // below this counts as "silent"
const SILENCE_TIMEOUT_MS = 12000;    // auto-stop after 12s of continuous silence
const MIN_RECORDING_MS = 1500;       // never auto-stop before this many ms — prevents premature cutoffs
const MAX_RECORDING_MS = 90000;      // hard cap 90s per capture

function playTone(ctx, freq, durMs) {
  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + durMs / 1000);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + durMs / 1000 + 0.02);
  } catch { /* tone is non-critical */ }
}

export default function FloatingMicButton({ sessionId, onSaved, preTaggedMuscle, preTaggedSide, openFromExternalTrigger, onExternalConsumed, onActiveChange }) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [draft, setDraft] = useState(null); // structured capture awaiting confirmation
  const [level, setLevel] = useState(0); // 0..1 audio level
  const [elapsed, setElapsed] = useState(0); // ms since record start

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const lastVoiceAtRef = useRef(0);
  const recordStartAtRef = useRef(0);
  const stopTriggeredRef = useRef(false);

  const supported = typeof window !== "undefined" && !!navigator?.mediaDevices?.getUserMedia && typeof window.MediaRecorder !== "undefined";

  // Bubble recording state to the parent (lets the workspace pulse the inline button)
  useEffect(() => { onActiveChange?.(recording || processing); }, [recording, processing, onActiveChange]);

  const cleanup = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    try { analyserRef.current?.disconnect(); } catch { /* noop */ }
    analyserRef.current = null;
    try { audioCtxRef.current?.close(); } catch { /* noop */ }
    audioCtxRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setLevel(0);
    setElapsed(0);
  }, []);

  // Internal: actually stop the recorder
  const triggerStop = useCallback(() => {
    if (stopTriggeredRef.current) return;
    stopTriggeredRef.current = true;
    if (recorderRef.current && recorderRef.current.state === "recording") {
      recorderRef.current.stop();
    }
    setRecording(false);
  }, []);

  const beginRecording = useCallback(async () => {
    if (!supported) {
      toast.error("Voice capture isn't supported in this browser. Try Chrome or Safari.");
      return;
    }
    stopTriggeredRef.current = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
                 : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4"
                 : "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];

      // Web Audio analyser for level + silence detection
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.7;
      analyserRef.current = analyser;
      source.connect(analyser);

      playTone(ctx, 880, 110); // start tone

      const buf = new Float32Array(analyser.fftSize);
      const tick = () => {
        if (!analyserRef.current) return;
        analyser.getFloatTimeDomainData(buf);
        // Compute RMS
        let sum = 0;
        for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
        const rms = Math.sqrt(sum / buf.length);
        setLevel(Math.min(1, rms * 6));
        const now = performance.now();
        setElapsed(now - recordStartAtRef.current);
        if (rms > SILENCE_RMS_THRESHOLD) lastVoiceAtRef.current = now;
        const silenceFor = now - lastVoiceAtRef.current;
        const sinceStart = now - recordStartAtRef.current;
        if (sinceStart >= MAX_RECORDING_MS) {
          triggerStop();
          return;
        }
        if (sinceStart >= MIN_RECORDING_MS && silenceFor >= SILENCE_TIMEOUT_MS) {
          triggerStop();
          return;
        }
        rafRef.current = requestAnimationFrame(tick);
      };

      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        cleanup();
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        // Stop tone (lower pitch)
        try {
          const Ctx2 = window.AudioContext || window.webkitAudioContext;
          const c2 = new Ctx2();
          playTone(c2, 540, 130);
          setTimeout(() => { try { c2.close(); } catch { /* noop */ } }, 250);
        } catch { /* noop */ }
        if (blob.size < 600) {
          toast.message("That was too short. Try again and dictate at least a sentence.");
          return;
        }
        setProcessing(true);
        try {
          const ext = (mime.includes("mp4")) ? "mp4" : "webm";
          const fd = new FormData();
          fd.append("audio", blob, `capture.${ext}`);
          const t = await api.post("/clinical-capture/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          const transcript = (t.data?.transcript || "").trim();
          if (!transcript) {
            toast.error("Could not transcribe — please try again.");
            return;
          }
          const s = await api.post("/clinical-capture/structure", {
            transcript,
            pre_tagged_muscle: preTaggedMuscle || null,
            pre_tagged_side: preTaggedSide || null,
          });
          setDraft({ ...s.data, raw_transcript: transcript });
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Capture failed.");
        } finally {
          setProcessing(false);
        }
      };
      recorderRef.current = rec;
      rec.start();
      recordStartAtRef.current = performance.now();
      lastVoiceAtRef.current = performance.now();
      setRecording(true);
      tick();
    } catch (e) {
      console.error("[mic] getUserMedia failed", e);
      cleanup();
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        toast.error("Microphone blocked. Allow mic access in your browser, then try again.");
      } else if (name === "NotFoundError") {
        toast.error("No microphone detected. Plug one in or check system settings.");
      } else {
        toast.error("Couldn't access the microphone.");
      }
    }
  }, [supported, preTaggedMuscle, preTaggedSide, cleanup, triggerStop]);

  const stopRecording = useCallback(() => {
    triggerStop();
  }, [triggerStop]);

  // Allow an external trigger (e.g., diagram tap) to start a recording
  useEffect(() => {
    if (openFromExternalTrigger && !recording && !processing && !draft) {
      const t = setTimeout(() => {
        beginRecording();
        onExternalConsumed?.();
      }, 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [openFromExternalTrigger, recording, processing, draft, beginRecording, onExternalConsumed]);

  // Stop the meter if the component unmounts mid-recording
  useEffect(() => () => cleanup(), [cleanup]);

  const saveDraft = async (capture, addToLongitudinal) => {
    try {
      const payload = { ...capture, add_to_longitudinal: addToLongitudinal };
      const r = await api.post(`/sessions/${sessionId}/captures`, payload);
      toast.success(addToLongitudinal ? "Capture saved + added to longitudinal record." : "Capture saved for this session.");
      setDraft(null);
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save capture.");
    }
  };

  const discardDraft = () => setDraft(null);

  if (!sessionId) return null;

  const seconds = Math.floor(elapsed / 1000);
  const ringStops = Math.min(20, Math.round(level * 20));

  return (
    <>
      {/* The floating button itself — hidden when a modal is up */}
      {!draft && (
        <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-2 print:hidden">
          {recording && (
            <div className="bg-ink-900/95 backdrop-blur text-white rounded-2xl px-4 py-2.5 shadow-2xl border border-ink-700 inline-flex items-center gap-3 text-sm" data-testid="mic-status-pill">
              <span className="inline-flex items-center gap-2">
                <span className="relative inline-flex">
                  <span className="absolute inset-0 rounded-full bg-terracotta-500 animate-ping opacity-75" />
                  <span className="relative w-2.5 h-2.5 rounded-full bg-terracotta-500" />
                </span>
                <span className="font-mono tabular-nums">{String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}</span>
              </span>
              {/* Live level meter */}
              <div className="flex items-end gap-[2px] h-5" data-testid="mic-level-meter" aria-label="Audio level">
                {Array.from({ length: 20 }).map((_, i) => (
                  <span
                    key={i}
                    className={`w-[3px] rounded-sm transition-all duration-75 ${i < ringStops ? (i > 14 ? "bg-terracotta-400" : i > 9 ? "bg-amber-400" : "bg-sage-400") : "bg-ink-700"}`}
                    style={{ height: `${4 + (i / 19) * 16}px` }}
                  />
                ))}
              </div>
              <span className="text-[10px] text-paper-300 hidden sm:inline">Auto-stops after 12s silence</span>
            </div>
          )}
          <button
            data-testid="floating-mic-btn"
            onClick={recording ? stopRecording : beginRecording}
            disabled={processing}
            aria-label={recording ? "Stop recording" : "Record clinical capture"}
            className={`w-16 h-16 rounded-full shadow-lg flex items-center justify-center transition-all
              ${recording ? "bg-terracotta-500 hover:bg-terracotta-600 text-white scale-110 ring-4 ring-terracotta-500/30" : "bg-ink-900 hover:bg-ink-700 text-white"}
              ${processing ? "opacity-60 cursor-wait" : ""}`}
            title={recording ? "Stop & transcribe" : "Tap to dictate a finding"}
          >
            {processing ? <Loader2 className="w-6 h-6 animate-spin" /> : recording ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
            {recording && (
              <span className="absolute -top-1 -right-1 bg-white text-terracotta-600 text-[10px] font-bold px-1.5 py-0.5 rounded-full">REC</span>
            )}
          </button>
        </div>
      )}

      {draft && (
        <CaptureConfirmModal
          draft={draft}
          onChange={setDraft}
          onSave={saveDraft}
          onDiscard={discardDraft}
        />
      )}
    </>
  );
}

function CaptureConfirmModal({ draft, onChange, onSave, onDiscard }) {
  const [showRaw, setShowRaw] = useState(true);
  const set = (k, v) => onChange({ ...draft, [k]: v });

  return (
    <div className="fixed inset-0 z-50 bg-ink-900/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto print:hidden" data-testid="capture-confirm-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-2xl max-h-[92vh] sm:max-h-[88vh] overflow-y-auto relative">
        <div className="sticky top-0 bg-white border-b border-paper-200 px-5 sm:px-7 py-4 flex items-center justify-between">
          <div>
            <div className="label-overline text-sage-700">Live clinical capture</div>
            <h2 className="font-heading text-xl sm:text-2xl text-ink-900">Review &amp; confirm finding</h2>
          </div>
          <button onClick={onDiscard} className="w-9 h-9 rounded-full hover:bg-paper-100 flex items-center justify-center" data-testid="capture-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 sm:px-7 py-5 space-y-4">
          {/* Raw transcript — shown by default now */}
          <div className="bg-paper-50 rounded-2xl border border-paper-200 p-3">
            <button onClick={() => setShowRaw((v) => !v)} className="w-full flex items-center gap-2 text-xs text-ink-500 uppercase tracking-wider" data-testid="capture-show-raw">
              {showRaw ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />} Transcript preview
            </button>
            {showRaw && (
              <div className="text-sm text-ink-700 mt-2 italic" data-testid="capture-raw-text">&ldquo;{draft.raw_transcript}&rdquo;</div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Body region" testid="cap-body-region" value={draft.body_region} onChange={(v) => set("body_region", v)} placeholder="e.g. lower lumbar" />
            <SelectField label="Side" testid="cap-side" value={draft.side || ""} onChange={(v) => set("side", v || null)} options={[
              { v: "", label: "—" },
              { v: "left", label: "Left" },
              { v: "right", label: "Right" },
              { v: "bilateral", label: "Bilateral" },
            ]} />
            <Field label="Tissue quality" testid="cap-tissue-quality" value={draft.tissue_quality} onChange={(v) => set("tissue_quality", v)} placeholder="dense banding, hypertonic…" />
            <Field label="Location / size" testid="cap-location-size" value={draft.location_size} onChange={(v) => set("location_size", v)} placeholder="~4 inches wide" />
            <SeverityField value={draft.severity} onChange={(v) => set("severity", v)} />
            <ChipField label="Suspected muscles" testid="cap-muscles" items={draft.suspected_muscles} onChange={(v) => set("suspected_muscles", v)} placeholder="e.g. ql-left" />
          </div>

          <Field label="Technique used" testid="cap-technique" value={draft.technique_used} onChange={(v) => set("technique_used", v)} placeholder="compression + slow myofascial release" />
          <Field label="Client response" testid="cap-client-response" value={draft.client_response} onChange={(v) => set("client_response", v)} placeholder="tissue softened, breath deepened…" />
          <Field label="Your reasoning" testid="cap-reasoning" value={draft.therapist_reasoning} onChange={(v) => set("therapist_reasoning", v)} placeholder="why you suspect this pattern…" />
          <Field label="Recheck next session" testid="cap-recheck" value={draft.recheck_item} onChange={(v) => set("recheck_item", v)} placeholder="re-test pelvic rotation, recheck ROM…" />
        </div>

        <div className="sticky bottom-0 bg-white border-t border-paper-200 px-5 sm:px-7 py-4 flex items-center justify-between gap-2 flex-wrap">
          <button onClick={onDiscard} className="min-h-[44px] px-5 rounded-full text-ink-700 hover:bg-paper-100 inline-flex items-center gap-2" data-testid="capture-discard">
            <Trash2 className="w-4 h-4" /> Discard
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => onSave(draft, false)}
              className="min-h-[44px] px-5 rounded-full border border-paper-200 hover:bg-paper-100 text-ink-900 font-medium inline-flex items-center gap-2"
              data-testid="capture-save-session-only"
            >
              Save to session only
            </button>
            <button
              onClick={() => onSave(draft, true)}
              className="min-h-[44px] px-5 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium inline-flex items-center gap-2"
              data-testid="capture-save-with-longitudinal"
            >
              <Check className="w-4 h-4" /> Save + add to longitudinal
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, placeholder }) {
  return (
    <div>
      <label className="label-overline block mb-1">{label}</label>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full min-h-[44px] px-3 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none"
      />
    </div>
  );
}

function SelectField({ label, testid, value, onChange, options }) {
  return (
    <div>
      <label className="label-overline block mb-1">{label}</label>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full min-h-[44px] px-3 rounded-xl bg-paper-50 border border-paper-200 outline-none"
      >
        {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>
    </div>
  );
}

function SeverityField({ value, onChange }) {
  return (
    <div>
      <label className="label-overline block mb-1">Severity (0–10)</label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={0}
          max={10}
          value={value == null ? "" : value}
          onChange={(e) => onChange(e.target.value === "" ? null : Math.max(0, Math.min(10, parseInt(e.target.value) || 0)))}
          data-testid="cap-severity"
          className="w-24 min-h-[44px] px-3 rounded-xl bg-paper-50 border border-paper-200 outline-none"
          placeholder="—"
        />
        <span className="text-xs text-ink-500">leave blank if not assessed</span>
      </div>
    </div>
  );
}

function ChipField({ label, testid, items = [], onChange, placeholder }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim();
    if (!v) return;
    onChange([...(items || []), v]);
    setInput("");
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div>
      <label className="label-overline block mb-1">{label}</label>
      <div className="flex flex-wrap gap-1 mb-2" data-testid={testid}>
        {(items || []).map((m, i) => (
          <span key={`${m}-${i}`} className="text-xs px-2 py-1 rounded-full bg-sage-50 text-sage-700 border border-sage-100 inline-flex items-center gap-1">
            {m}
            <button onClick={() => remove(i)} className="hover:text-destructive" aria-label={`Remove ${m}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          data-testid={`${testid}-input`}
          className="flex-1 min-h-[40px] px-3 rounded-xl bg-paper-50 border border-paper-200 outline-none text-sm"
        />
        <button onClick={add} className="min-h-[40px] px-3 rounded-full bg-paper-100 hover:bg-paper-200 text-sm" data-testid={`${testid}-add`}>+ Add</button>
      </div>
    </div>
  );
}
